/**
 * AddressLocator - overlay search control for the District Map.
 *
 * Lets a user type an address (with typeahead suggestions) and find which AIU
 * member district it falls within. Geocoding is provider-agnostic (see
 * api/geocoding.ts); the containing-district test runs client-side against the
 * polygons the map already loaded. Designed for residency / enrollment checks by
 * district administrative staff.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import { Alert, Spinner } from 'flowbite-react';
import { useAuth } from '../../contexts/AuthContext';
import { getGeocodingProvider, type AddressSuggestion } from '../../api/geocoding';
import { reverseSchoolDistrict, type CensusDistrict } from '../../api/censusGeographies';
import { mapApi } from '../../api/map';
import type {
  DistrictPolygonsResponse,
  DistrictPolygonFeature,
  SchoolPointsResponse,
} from '../../api/map';
import { getUserErrorMessage } from '../../api/client';
import { findContainingDistricts, nearestSchools, type NearbySchool } from './geoUtils';

interface LocatorColors {
  red: string;
  dark: string;
}

interface AddressLocatorProps {
  map: L.Map | null;
  polygons: DistrictPolygonsResponse | null;
  schools: SchoolPointsResponse | null;
  colors: LocatorColors;
  /** Notifies the parent which district (educationOrganizationId) to highlight, or null. */
  onLocatedDistrict: (educationOrganizationId: number | null) => void;
}

interface LookupResult {
  label: string;
  latitude: number;
  longitude: number;
  districts: DistrictPolygonFeature[];
  nearby: NearbySchool[];
  municipality?: string;
  county?: string;
  censusDistrict: CensusDistrict | null;
  /** true/false for org admins; null when not applicable (superAdmin/viewer). */
  inMyDistrict: boolean | null;
}

const pinHtml = (color: string): string => `
<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 24 32">
  <path fill="${color}" stroke="white" stroke-width="1.5"
    d="M12 1C6.5 1 2 5.5 2 11c0 7 10 19 10 19s10-12 10-19C22 5.5 17.5 1 12 1z"/>
  <circle cx="12" cy="11" r="3.5" fill="white"/>
</svg>`;

export const AddressLocator: React.FC<AddressLocatorProps> = ({
  map,
  polygons,
  schools,
  colors,
  onLocatedDistrict,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const provider = useMemo(() => getGeocodingProvider(), []);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LookupResult | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestAbortRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  // Close the suggestions dropdown on outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const runSuggest = useCallback(
    async (value: string) => {
      suggestAbortRef.current?.abort();
      const ctrl = new AbortController();
      suggestAbortRef.current = ctrl;
      setSearching(true);
      try {
        const items = await provider.suggest(value, ctrl.signal);
        setSuggestions(items);
        setShowSuggestions(true);
      } catch (err) {
        if ((err as Error)?.name !== 'AbortError') {
          setError('Address search is unavailable right now. Please try again.');
        }
      } finally {
        if (suggestAbortRef.current === ctrl) setSearching(false);
      }
    },
    [provider]
  );

  const handleInput = (value: string) => {
    setQuery(value);
    setError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSuggest(value), 300);
  };

  const computeResult = useCallback(
    async (s: AddressSuggestion) => {
      const lat = s.latitude as number;
      const lng = s.longitude as number;
      const districts = polygons ? findContainingDistricts(lng, lat, polygons) : [];
      const nearby = nearestSchools(lat, lng, schools, 3);

      const myOrgIds =
        currentUser?.role === 'organizationAdmin' ? currentUser.organizationIds || [] : [];
      const inMyDistrict =
        myOrgIds.length > 0
          ? districts.some((d) => myOrgIds.includes(d.properties.educationOrganizationId))
          : null;

      // Only ask Census who owns this point when no AIU member district matched.
      const censusDistrict = districts.length === 0 ? await reverseSchoolDistrict(lat, lng) : null;

      setResult({
        label: s.label,
        latitude: lat,
        longitude: lng,
        districts,
        nearby,
        municipality: s.municipality,
        county: s.county,
        censusDistrict,
        inMyDistrict,
      });
      onLocatedDistrict(districts[0]?.properties.educationOrganizationId ?? null);

      // Privacy-safe, fire-and-forget audit log (no raw address).
      mapApi
        .logLookup({
          matched: districts.length > 0,
          leaId: districts[0]?.properties.leaId ?? null,
          districtName: districts[0]?.properties.name ?? censusDistrict?.name ?? null,
          outOfArea: districts.length === 0,
          provider: provider.name,
        })
        .catch(() => {
          /* non-fatal */
        });
    },
    [polygons, schools, currentUser, onLocatedDistrict, provider.name]
  );

  const handleSelect = async (s: AddressSuggestion) => {
    setShowSuggestions(false);
    setError(null);
    setResolving(true);
    try {
      const resolved = await provider.resolve(s);
      if (resolved.latitude == null || resolved.longitude == null) {
        throw new Error('Could not pinpoint that address.');
      }
      setQuery(resolved.label);
      await computeResult(resolved);
    } catch (err) {
      setError(getUserErrorMessage(err, 'Could not locate that address.'));
    } finally {
      setResolving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault();
      void handleSelect(suggestions[0]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setResult(null);
    setError(null);
    onLocatedDistrict(null);
  };

  // Drop a pin and fly to the located point.
  useEffect(() => {
    if (!map) return;
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
    if (!result) return;
    const icon = L.divIcon({
      className: 'address-pin',
      html: pinHtml(colors.red),
      iconSize: [30, 40],
      iconAnchor: [15, 38],
    });
    markerRef.current = L.marker([result.latitude, result.longitude], {
      icon,
      zIndexOffset: 1000,
    }).addTo(map);
    map.flyTo([result.latitude, result.longitude], Math.max(map.getZoom(), 13), {
      duration: 0.6,
    });
    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    };
  }, [map, result, colors.red]);

  const busy = searching || resolving;

  return (
    <div
      ref={containerRef}
      className="absolute top-3 left-3 z-[1000] w-80 max-w-[calc(100%-1.5rem)]"
    >
      {/* Search box */}
      <div className="relative">
        <div className="flex items-center gap-2 rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 px-3 py-2">
          <svg
            className="h-4 w-4 shrink-0 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z"
            />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => handleInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder="Find a district by address…"
            autoComplete="off"
            className="w-full bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
          />
          {busy && <Spinner size="sm" />}
          {!busy && (query || result) && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear"
              className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10">
            {suggestions.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => handleSelect(s)}
                className="block w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <Alert color="failure" className="mt-2" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Result card */}
      {result && (
        <div className="mt-2 rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 p-3 text-sm">
          <p className="text-xs text-gray-500 dark:text-gray-400 break-words">{result.label}</p>

          {/* Matched district(s) */}
          {result.districts.length === 1 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => navigate(`/leas/${result.districts[0].properties.leaId}`)}
                className="text-left font-semibold text-blue-700 dark:text-blue-400 hover:underline"
              >
                {result.districts[0].properties.name}
              </button>
              <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                {result.districts[0].properties.leaType && (
                  <div>{result.districts[0].properties.leaType}</div>
                )}
                {result.districts[0].properties.enrollment != null && (
                  <div>
                    Enrollment: {result.districts[0].properties.enrollment.toLocaleString()}
                  </div>
                )}
                {result.districts[0].properties.superintendentName && (
                  <div>Supt: {result.districts[0].properties.superintendentName}</div>
                )}
              </div>
            </div>
          )}

          {result.districts.length > 1 && (
            <div className="mt-2">
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                Multiple districts overlap here
              </p>
              <ul className="mt-1 list-disc pl-5 text-xs text-gray-600 dark:text-gray-400">
                {result.districts.map((d) => (
                  <li key={d.properties.educationOrganizationId}>
                    <button
                      type="button"
                      onClick={() => navigate(`/leas/${d.properties.leaId}`)}
                      className="text-blue-700 dark:text-blue-400 hover:underline"
                    >
                      {d.properties.name}
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                Boundaries overlap near this point — verify manually.
              </p>
            </div>
          )}

          {result.districts.length === 0 && (
            <div className="mt-2 rounded-md bg-amber-50 dark:bg-amber-900/30 p-2 text-xs text-amber-800 dark:text-amber-300">
              Not within an AIU member district.
              {result.censusDistrict && (
                <>
                  {' '}
                  This address appears to be in <strong>{result.censusDistrict.name}</strong>, which
                  isn’t an AIU member district.
                </>
              )}
            </div>
          )}

          {/* In-my-district banner (org admins) */}
          {result.inMyDistrict === true && (
            <div className="mt-2 rounded-md bg-green-50 dark:bg-green-900/30 px-2 py-1 text-xs font-medium text-green-800 dark:text-green-300">
              ✓ Within your district
            </div>
          )}
          {result.inMyDistrict === false && (
            <div className="mt-2 rounded-md bg-red-50 dark:bg-red-900/30 px-2 py-1 text-xs font-medium text-red-800 dark:text-red-300">
              ✗ Not within your district
            </div>
          )}

          {/* Municipality / county */}
          {(result.municipality || result.county) && (
            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              {[result.municipality, result.county].filter(Boolean).join(', ')}
            </div>
          )}

          {/* Nearest schools */}
          {result.nearby.length > 0 && (
            <div className="mt-2 border-t border-gray-100 dark:border-gray-700 pt-2">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                Nearest schools
              </p>
              <ul className="mt-1 space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                {result.nearby.map((n) => (
                  <li key={n.feature.properties.schoolId} className="flex justify-between gap-2">
                    <span className="min-w-0 truncate">
                      {n.feature.properties.name}
                      {n.feature.properties.districtName && (
                        <span className="text-gray-400 dark:text-gray-500">
                          {' '}
                          ({n.feature.properties.districtName})
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {n.distanceMiles.toFixed(1)} mi
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-2 border-t border-gray-100 dark:border-gray-700 pt-2 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
            District-level result from WPRDC boundaries — a guide, not an official residency
            determination.
          </p>
        </div>
      )}
    </div>
  );
};

export default AddressLocator;
